import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import getPersonFormName from '@/components/OnboardingTracker/getPersonFormName';

/**
 * Localized onboarding-app template names for the Application Details drawer's Forms and Documents
 * tabs. The backend sends each item's template `Identifier` and its English `Name`: the identifier is
 * the logic key, and an identifier this map doesn't know (a merchant-specific template set) or a `null`
 * one (an onboarding app version that doesn't report it) falls back to `Name`.
 *
 * English values are the onboarding app's own names, verbatim, so English reads as the onboarding app
 * shows it.
 *
 * Memoized on `t`, whose identity changes only when the language does.
 */
export default function useOnboardingTemplateName() {
  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UseOnboardingTemplateName' });

  return useMemo(() => {
    const formNames = new Map<string, string>([
      ['business_details', t('Forms.businessDetails', { defaultValue: 'Business Details' })],
      ['financial_details', t('Forms.financialDetails', { defaultValue: 'Financial Details' })],
      ['lender_information', t('Forms.lenderInformation', { defaultValue: 'Lender Information' })],
      [
        'entity_structure',
        t('Forms.entityStructure', { defaultValue: 'Entity Structure and Key Stakeholder Structure' }),
      ],
      ['ownership_structure', t('Forms.ownershipStructure', { defaultValue: 'Ownership Structure Details' })],
      ['trust_structure', t('Forms.trustStructure', { defaultValue: 'Trust Structure Details' })],
      ['key_controller', t('Forms.keyController', { defaultValue: 'Key Controller Details' })],
      ['partnership_structure', t('Forms.partnershipStructure', { defaultValue: 'Partnership Structure Details' })],
      ['signing_authorities', t('Forms.signingAuthorities', { defaultValue: 'Signing Authority Details' })],
      ['account_billing', t('Forms.accountBilling', { defaultValue: 'Account & Billing' })],
      [
        'support_and_contact_info',
        t('Forms.supportAndContactInfo', { defaultValue: 'Support and Contact Information' }),
      ],
      ['supplementary_information', t('Forms.supplementaryInformation', { defaultValue: 'Supplementary Information' })],
      ['third_party_declaration', t('Forms.thirdPartyDeclaration', { defaultValue: 'Third-Party Declaration' })],
      [
        'business_information_agreement_detail',
        t('Forms.businessInformationAgreementDetail', {
          defaultValue: 'Step 1: Business Information & Legal Agreement Details',
        }),
      ],
    ]);

    const documentNames = new Map<string, string>([
      [
        'certificate_incorporation',
        t('Documents.certificateIncorporation', { defaultValue: 'Certificate of Incorporation' }),
      ],
      ['articles_incorporation', t('Documents.articlesIncorporation', { defaultValue: 'Articles of Incorporation' })],
      [
        'certificate_articles_name_change',
        t('Documents.certificateArticlesNameChange', { defaultValue: 'Certificate and Articles of name change' }),
      ],
      ['dba_supporting_document', t('Documents.dbaSupportingDocument', { defaultValue: 'DBA supporting documents' })],
      ['shareholder_registry', t('Documents.shareholderRegistry', { defaultValue: 'Shareholder Registry' })],
      ['board_resolution', t('Documents.boardResolution', { defaultValue: 'Board Resolution' })],
      ['annual_tax_return', t('Documents.annualTaxReturn', { defaultValue: 'Annual Tax Returns' })],
      [
        'audited_financial_statements',
        t('Documents.auditedFinancialStatements', { defaultValue: 'Financial Statements' }),
      ],
      ['statutory_demand', t('Documents.statutoryDemand', { defaultValue: 'Statutory demand or legal proceedings' })],
      ['description_funding', t('Documents.descriptionFunding', { defaultValue: 'Description of funding/financing' })],
      ['list_service_providers', t('Documents.listServiceProviders', { defaultValue: 'List of service providers' })],
      ['lender_license', t('Documents.lenderLicense', { defaultValue: 'Lender licence' })],
      ['loan_agreement', t('Documents.loanAgreement', { defaultValue: 'Sample Loan Agreement' })],
      ['charter', t('Documents.charter', { defaultValue: 'Charter' })],
      ['trust_deed', t('Documents.trustDeed', { defaultValue: 'Trust Deed' })],
      ['property_trust', t('Documents.propertyTrust', { defaultValue: 'The property of Trust' })],
      ['master_business', t('Documents.masterBusiness', { defaultValue: 'Master business license' })],
      [
        'invoices_service_agreements',
        t('Documents.invoicesServiceAgreements', { defaultValue: 'Invoices/Service Agreements' }),
      ],
      [
        'statements_directors',
        t('Documents.statementsDirectors', { defaultValue: 'Statement of directors, and registered office' }),
      ],
      ['constitution_bylaws', t('Documents.constitutionBylaws', { defaultValue: 'Constitution and bylaws' })],
      ['internal_controls', t('Documents.internalControls', { defaultValue: 'Internal controls' })],
      [
        'standards_beneficiaries_disbursements',
        t('Documents.standardsBeneficiariesDisbursements', {
          defaultValue: 'Standards for beneficiaries and disbursements',
        }),
      ],
      [
        'registration_documentation',
        t('Documents.registrationDocumentation', { defaultValue: 'Registration documentation' }),
      ],
      ['mission_activities', t('Documents.missionActivities', { defaultValue: 'Org Mission and Activities' })],
      [
        'canadian_registered_charity',
        t('Documents.canadianRegisteredCharity', { defaultValue: 'Canadian registered charity (T3010)' }),
      ],
      [
        'registered_declaration_partnership',
        t('Documents.registeredDeclarationPartnership', { defaultValue: 'Registered declaration of partnership' }),
      ],
      [
        'statement_account_source_deductions',
        t('Documents.statementAccountSourceDeductions', {
          defaultValue: 'Statement of account for source deductions (PD7A)',
        }),
      ],
      [
        'information_security_program',
        t('Documents.informationSecurityProgram', { defaultValue: 'Information security program' }),
      ],
      ['privacy_program', t('Documents.privacyProgram', { defaultValue: 'Privacy program' })],
      ['current_litigations', t('Documents.currentLitigations', { defaultValue: 'Current litigations' })],
      ['ownership_chart', t('Documents.ownershipChart', { defaultValue: 'Ownership Structure' })],
      ['regulatory_registration', t('Documents.regulatoryRegistration', { defaultValue: 'Regulatory Registration' })],
      [
        'aml_atf_sanctions_policies',
        t('Documents.amlATFSanctionsPolicies', { defaultValue: 'AML / ATF / Sanctions Policies & Procedures' }),
      ],
      ['underwriting_process', t('Documents.underwritingProcess', { defaultValue: 'Underwriting Process' })],
      [
        'independent_review_aml_aft',
        // The onboarding app's name really has the double space.
        t('Documents.independentReviewAMLAFT', { defaultValue: 'Independent Review of AML / ATF  Program' }),
      ],
      [
        'aml_aft_sanctions_training_program',
        t('Documents.amlAFTSanctionsTrainingProgram', { defaultValue: 'AML / ATF / Sanctions Training Program' }),
      ],
      ['aml_atf_procedures', t('Documents.amlATFProcedures', { defaultValue: 'AML/ ATF Procedures' })],
      [
        'regulatory_examination',
        t('Documents.regulatoryExamination', { defaultValue: 'Regulatory Examination reports' }),
      ],
      [
        'individual_responsible_regulatory_compliance',
        t('Documents.individualResponsibleRegulatoryCompliance', {
          defaultValue: 'Designated individual responsible for regulatory compliance',
        }),
      ],
      [
        'it_information_security',
        t('Documents.itInformationSecurity', { defaultValue: 'IT and information security' }),
      ],
      [
        'supplementary_documentation',
        t('Documents.supplementaryDocumentation', { defaultValue: 'Supplementary Documentation' }),
      ],
      ['register_directors', t('Documents.registerDirectors', { defaultValue: 'Register of Directors' })],
      [
        'valid_business_license',
        t('Documents.validBusinessLicense', { defaultValue: 'Valid Current Business Licence' }),
      ],
      [
        'form_w9_tax_declaration',
        t('Documents.formW9TaxDeclaration', { defaultValue: 'Form W-9 -or Tax Declaration' }),
      ],
    ]);

    /** The per-person form name with its prefix translated and the person's name kept as sent. */
    function resolvePersonFormName(identifier: string, personName: string): string | null {
      switch (identifier) {
        case 'signing_authorities':
          return t('PersonForms.signingAuthority', { defaultValue: 'Signing Authority {{personName}}', personName });
        case 'ownership_structure':
          return t('PersonForms.owner', { defaultValue: 'Owner {{personName}}', personName });
        case 'trust_structure':
          return t('PersonForms.trust', { defaultValue: 'Trust {{personName}}', personName });
        case 'key_controller':
          return t('PersonForms.keyController', { defaultValue: 'Key Controller {{personName}}', personName });
        case 'partnership_structure':
          return t('PersonForms.partner', { defaultValue: 'Partner {{personName}}', personName });
        default:
          return null;
      }
    }

    return {
      /**
       * A form's display name. A per-person form ("Signing Authority Jane Doe") keeps the person's name
       * and translates only the prefix; translating it by identifier alone would give every signer the
       * same label. Its template's own name ("Signing Authority Details") also starts with the prefix,
       * so the template name is matched exactly before any prefix is stripped.
       */
      resolveFormName: (identifier: string | null, name: string): string => {
        if (identifier === null) return name;

        const personName = getPersonFormName(identifier, name);

        if (personName) return resolvePersonFormName(identifier, personName) ?? name;

        return formNames.get(identifier) ?? name;
      },
      /** A document's display name; unknown or `null` identifiers fall back to `name`. */
      resolveDocumentName: (identifier: string | null, name: string): string =>
        (identifier === null ? undefined : documentNames.get(identifier)) ?? name,
    };
  }, [t]);
}
